import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Badge } from '@/components/ui/badge'
import { Trash2, Clock, CheckCircle, XCircle, FileText } from 'lucide-react'
import { useEmailDrafterStore } from '@/stores/email-drafter'
import type { HistoryItem } from '@/types/email-drafter'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'

export function HistoryPanel() {
  const { history, loadHistoryItem, deleteHistoryItem, clearHistory } = useEmailDrafterStore()

  const handleLoadItem = (item: HistoryItem) => {
    loadHistoryItem(item)
    // Scroll to top to show loaded content
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const formatResponseTime = (ms?: number) => {
    if (!ms) return '-'
    if (ms < 1000) return `${ms}ms`
    return `${(ms / 1000).toFixed(1)}s`
  }

  return (
    <Card className="h-full">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-lg">Test Verlauf</CardTitle>
        {history.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (confirm('Möchten Sie wirklich den gesamten Verlauf löschen?')) {
                clearHistory()
              }
            }}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Alle löschen
          </Button>
        )}
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="h-[calc(100vh-200px)]">
          {history.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-2 opacity-30" />
              <p>Keine Tests im Verlauf</p>
            </div>
          ) : (
            <div className="space-y-2 p-4">
              {history.map((item) => (
                <div
                  key={item.id}
                  className="border rounded-lg p-3 hover:bg-accent/50 transition-colors cursor-pointer group"
                  onClick={() => handleLoadItem(item)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {item.status === 'success' ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-500" />
                      )}
                      <Badge variant={item.status === 'success' ? 'default' : 'destructive'}>
                        {item.status === 'success' ? 'Erfolgreich' : 'Fehler'}
                      </Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 opacity-0 group-hover:opacity-100"
                      onClick={(e) => {
                        e.stopPropagation()
                        deleteHistoryItem(item.id)
                      }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>

                  <div className="space-y-1">
                    <p className="text-sm font-medium truncate">
                      {item.request.email_name}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {item.request.email_address}
                    </p>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {item.request.email_content}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    <span>
                      {format(new Date(item.timestamp), 'dd.MM.yyyy HH:mm', { locale: de })}
                    </span>
                    {item.responseTime && (
                      <>
                        <span>•</span>
                        <span>{formatResponseTime(item.responseTime)}</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  )
}